// ============================================================================
// BOQ — Projects index (briotime as source of truth, boq.projects as mirror)
// ============================================================================
// Briotime's public.projects holds the canonical code/name/category/status.
// boq.projects is a per-row sidecar keyed on project_code that carries the
// BOQ-only bits (scheme_stage, city, the uuid id that the rest of boq.* FKs
// against). The mirror is upserted lazily — created the first time a user
// sets a BOQ-specific field on a briotime project.
//
// Both queries are independent; merged in JS by project_code. With ~100
// briotime projects this is fine — no need for a SQL view.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError, normalizeId } from '../../hooks/useSupabase';

const BRIOTIME_COLUMNS = 'id, project_code, name, category, status, client_id, contract_currency';
const BOQ_COLUMNS =
  'id, project_code, name, city, typology, status, scheme_stage, ' +
  'built_up_area, carpet_area, construction_area, uses_cost_split, ' +
  'created_at, updated_at, deleted_at';

const mergeRows = (briotimeRows, boqRows) => {
  const boqByCode = new Map();
  (boqRows || []).forEach((b) => {
    if (b.deleted_at) return;
    boqByCode.set(b.project_code, b);
  });

  return (briotimeRows || []).map((p) => {
    const mirror = boqByCode.get(p.project_code) || null;
    return {
      briotimeId: normalizeId(p.id),
      projectCode: p.project_code,
      name: p.name,
      category: p.category || null,
      briotimeStatus: p.status || null,
      clientId: normalizeId(p.client_id),
      currency: p.contract_currency || 'INR',
      hasMirror: !!mirror,
      boqId: mirror?.id || null,
      city: mirror?.city || null,
      schemeStage: mirror?.scheme_stage || null,
      builtUpArea: mirror?.built_up_area == null ? null : Number(mirror.built_up_area),
      carpetArea: mirror?.carpet_area == null ? null : Number(mirror.carpet_area),
      constructionArea: mirror?.construction_area == null ? null : Number(mirror.construction_area),
      usesCostSplit: !!mirror?.uses_cost_split,
      boqStatus: mirror?.status || null,
      boqUpdatedAt: mirror?.updated_at || null,
    };
  });
};

// enabled: hold the fetch until there is a signed-in session. A fetch made
// from the login screen runs as anon and comes back empty or refused.
export const useBoqProjects = ({ enabled = true } = {}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [briotime, boq] = await Promise.all([
        supabase
          .from('projects')
          .select(BRIOTIME_COLUMNS)
          .is('deleted_at', null)
          .not('project_code', 'is', null)
          .order('project_code'),
        supabase
          .schema('boq')
          .from('projects')
          .select(BOQ_COLUMNS)
          .is('deleted_at', null),
      ]);
      if (briotime.error) throw briotime.error;
      if (boq.error) throw boq.error;
      setProjects(mergeRows(briotime.data, boq.data));
    } catch (err) {
      console.error('[useBoqProjects] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'projects' }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Upsert a BOQ mirror row for a briotime project. Keyed on project_code
  // (boq.projects has UNIQUE on project_code). Returns the merged row so the
  // caller can refresh just that row without a full refetch.
  const upsertBoqMirror = useCallback(async (projectCode, fields) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to update a project.' };
      }
      // name is required on boq.projects (NOT NULL); pass through from the
      // caller so we don't have to re-fetch briotime here.
      const payload = {
        project_code: projectCode,
        name: fields.name,
        city: fields.city?.trim() || null,
        typology: fields.typology || null,
        scheme_stage: fields.schemeStage || null,
        ...(fields.builtUpArea !== undefined ? { built_up_area: (fields.builtUpArea === '' || fields.builtUpArea === null) ? null : Number(fields.builtUpArea) } : {}),
        ...(fields.carpetArea !== undefined ? { carpet_area: (fields.carpetArea === '' || fields.carpetArea === null) ? null : Number(fields.carpetArea) } : {}),
        ...(fields.constructionArea !== undefined ? { construction_area: (fields.constructionArea === '' || fields.constructionArea === null) ? null : Number(fields.constructionArea) } : {}),
        // status NOT NULL DEFAULT 'active' on the table — let the default
        // apply on first insert; pass through on updates if provided.
        ...(fields.status ? { status: fields.status } : {}),
      };
      const { data, error: upsertErr } = await supabase
        .schema('boq')
        .from('projects')
        .upsert(payload, { onConflict: 'project_code' })
        .select(BOQ_COLUMNS)
        .single();
      if (upsertErr) throw upsertErr;

      // Refresh just this row in the local index
      setProjects((prev) => prev.map((p) => {
        if (p.projectCode !== projectCode) return p;
        return {
          ...p,
          hasMirror: true,
          boqId: data.id,
          city: data.city,
          schemeStage: data.scheme_stage,
          builtUpArea: data.built_up_area == null ? null : Number(data.built_up_area),
          carpetArea: data.carpet_area == null ? null : Number(data.carpet_area),
          constructionArea: data.construction_area == null ? null : Number(data.construction_area),
          boqStatus: data.status,
          boqUpdatedAt: data.updated_at,
        };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqProjects] upsert failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'project' }) };
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { projects, loading, error, refresh: fetchAll, upsertBoqMirror };
};
